import type {
  PolicyholderDto,
  PolcyholderListDto,
  AddPolicyholderDto,
  UpdatePolicyholderDto,
} from '../../base/common/dtos/policyholder-list.dto';
import type { BaseDataTableUrlParamsModel } from '../../base/common/models/base/base-data-table-url-params.model';
import appendUrlDataTableParamsT from '../../base/utils/append-url-data-table-params.t';

interface State {
  loading: boolean;
  policyholders: PolcyholderListDto;
  selectedPolicyholderId: number | null;
  selectedPolicyholder: PolicyholderDto | null;
  policyholderSelectSource: PolicyholderDto[];
}

export interface FetchPolicyholdersListParams extends BaseDataTableUrlParamsModel<PolicyholderDto> {}

export const usePolicyholdersStore = defineStore('policyholdersStore', {
  state: (): State => ({
    loading: false,
    policyholders: {
      data: [],
      total: 0,
    },
    selectedPolicyholderId: null,
    selectedPolicyholder: null,
    policyholderSelectSource: [],
  }),
  actions: {
    async fetchPolicyholdersList(options?: FetchPolicyholdersListParams): Promise<void> {
      const { $snapApi } = useNuxtApp();

      this.loading = true;

      let url = 'catalogs/contacts/datatable/policyholders';

      if (options) {
        url = appendUrlDataTableParamsT(url, options);
      }

      try {
        const response = await $snapApi({
          method: 'GET',
          url,
        });

        this.policyholders.data = response.data.list;
        this.policyholders.total = response.data.count;
      } catch {
      } finally {
        this.loading = false;
      }
    },
    async removeSelectedPolicyholder(): Promise<void> {
      const { $snapApi } = useNuxtApp();

      this.loading = true;

      const url = `catalogs/contacts/policyholders/${this.selectedPolicyholderId}`;

      try {
        await $snapApi({
          method: 'DELETE',
          url,
        });
      } catch {
      } finally {
        this.loading = false;
        this.selectedPolicyholderId = null;
      }
    },
    async fetchSelectedPolicyholder(): Promise<void> {
      if (!this.selectedPolicyholderId) {
        return;
      }

      const { $snapApi } = useNuxtApp();

      this.loading = true;

      const url = `catalogs/contacts/policyholders/${this.selectedPolicyholderId}`;

      try {
        const response = await $snapApi({
          method: 'GET',
          url,
        });

        this.selectedPolicyholder = response.data;
      } catch {
      } finally {
        this.loading = false;
      }
    },
    async saveNew(model: AddPolicyholderDto): Promise<void> {
      const body = model;

      const { $snapApi, $toastError, $toastSuccess } = useNuxtApp();

      this.loading = true;

      const url = `catalogs/contacts/policyholders`;

      try {
        await $snapApi({
          method: 'POST',
          url,
          data: body,
        });

        $toastSuccess('A new Policyholder is saved');
      } catch {
        $toastError('Failed to save a new Policyholder');
      } finally {
        this.loading = false;
      }
    },
    async saveExisting(model: UpdatePolicyholderDto): Promise<void> {
      const body = model;

      const { $snapApi, $toastError, $toastSuccess } = useNuxtApp();

      this.loading = true;

      const url = `catalogs/contacts/policyholders/${this.selectedPolicyholderId}`;

      try {
        await $snapApi({
          method: 'PUT',
          url,
          data: body,
        });

        $toastSuccess('An existing Policyholder is saved');
      } catch {
        $toastError('Failed to save a Policyholder');
      } finally {
        this.loading = false;
        this.selectedPolicyholderId = null;
      }
    },
    async fetchPolicyholderSelectSource(): Promise<void> {
      const { $snapApi } = useNuxtApp();

      this.loading = true;

      const url = `catalogs/contacts/policyholders`;

      try {
        const response = await $snapApi({
          method: 'GET',
          url,
        });

        this.policyholderSelectSource = response.data;
      } catch {
      } finally {
        this.loading = false;
      }
    },
  },
});
